import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { FormikProps } from 'formik'

import { useServiceRequest } from '../../hooks/useServiceRequest'
import { actions, selectors } from '../../redux'
import { RegisterFormProps } from '../../forms/RegisterForm'
import { UserProfile } from '../../rest/types/UserProfile'
import { Civility, UserSpeciality } from '../../rest/types/User'
import useMediaQuery from '../../hooks/useMediaQuery'
import { FormFieldOrientation } from '../../types/form'
import Icon, { Icons } from '../../components/Icon'

const useRegisterFormProps = ({
  profiles,
  specialities,
}: {
  profiles: Array<UserProfile>
  specialities: Array<UserSpeciality>
}) => {
  const { t } = useTranslation()
  const matches = useMediaQuery('(max-width: 576px)')
  const [isShownPassword, setIsShownPassword] = useState<boolean>(false)
  const [errorsTranslated, setErrors] =
    useState<{ label: string; field?: string | undefined }[]>()

  const formikRef = useRef<FormikProps<any>>(null)

  const [response, register] = useServiceRequest(
    selectors.auth.register,
    actions.auth.registerRequest,
    actions.auth.registerReset,
    () => formikRef.current?.resetForm()
  )

  const [responseYoungDoctor, handleSubmitForm] = useServiceRequest(
    selectors.mail.sendYoungDoctorEmail,
    actions.mail.sendYoungDoctorEmailRequest,
    actions.mail.sendYoungDoctorEmailReset
  )

  useEffect(() => {
    if (response.errors?.length) {
      const errorsTranslate = response.errors.map((error) => {
        return {
          ...error,
          label: t(`${error?.label?.replace(/ /g, '_').toLowerCase()}` as any),
        }
      })
      setErrors(errorsTranslate)
    }
  }, [response.errors, t])

  const props: RegisterFormProps = useMemo(
    () => ({
      innerRef: formikRef,
      validationSchema: Yup.object().shape({
        civility: Yup.string().required(t('yup_required')),
        firstName: Yup.string().required(t('yup_required')),
        lastName: Yup.string().required(t('yup_required')),
        email: Yup.string().required(t('yup_required')).email(t('yup_email')),
        profile: Yup.string().required(t('yup_required')),
        password: Yup.string().required(t('yup_required')),
      }),
      initialValues: {
        civility: '',
        firstName: '',
        lastName: '',
        email: '',
        profile: '',
        password: '',
        speciality: '',
        requestAccess: false,
        numberRpps: '',
        phone: '',
        installationProject: '',
      },
      title: t('register_form_title'),
      requestAccessTitle: t('register_form_request_access_title'),
      requestAccessText: t('register_form_request_access_text'),
      fields: {
        civility: {
          label: t('register_form_civility'),
          placeholder: t('register_form_civility_placeholder'),
          options: Object.values(Civility).map((value) => ({
            label: t(`civility_${value}`),
            value: value,
          })),
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
        },
        firstName: {
          label: t('register_form_firstname'),
          required: true,
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
        },
        lastName: {
          label: t('register_form_lastname'),
          required: true,
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
        },
        email: {
          label: t('register_form_email'),
          required: true,
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
        },
        profile: {
          label: t('register_form_profile'),
          placeholder: t('register_form_profile_placeholder'),
          required: true,
          options: profiles.map((profile) => ({
            label: profile.name,
            value: profile.id,
          })),
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
        },
        speciality: {
          label: t('register_form_speciality'),
          placeholder: t('register_form_speciality_placeholder'),
          required: true,
          options: specialities?.length
            ? specialities.map((speciality) => ({
                label: speciality.name,
                value: speciality.id,
              }))
            : [],
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
        },
        password: {
          label: t('register_form_password'),
          required: true,
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
          ...(!isShownPassword && {
            rightAdornment: (
              <Icon
                onClick={() =>
                  setIsShownPassword((isShownPassword) => !isShownPassword)
                }
                icon={Icons.eye}
                width={22}
                height={24}
              />
            ),
            type: 'password',
          }),
          ...(isShownPassword && {
            rightAdornment: (
              <Icon
                onClick={() =>
                  setIsShownPassword((isShownPassword) => !isShownPassword)
                }
                icon={Icons.eyeSlash}
                width={22}
                height={24}
              />
            ),
            type: 'text',
          }),
        },
        requestAccess: {
          label: t('register_form_request_access_label'),
        },
        numberRpps: {
          label: t('register_form_number_rpps'),
        },
        phone: {
          label: t('register_form_phone'),
          autoFormat: false,
        },
        installationProject: {
          label: t('register_form_installation_project'),
          placeholder: t('register_form_installation_project_placeholder'),
        },
      },
      submitButton: {
        text: t('register_submit'),
        isLoading: response?.pending && responseYoungDoctor?.pending,
      },
      onSubmit: (values) => {
        register({
          civility: values.civility,
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password,
          profile: values.profile,
          ...(values?.speciality && {
            speciality: values.speciality,
          }),
          email: values.email,
        })
        if (values.requestAccess) {
          handleSubmitForm({
            firstName: values.firstName,
            lastName: values.lastName,
            urpsId: values.numberRpps
              ? values.numberRpps
              : t('request_access_central_form_id_urps_default_value_mail'),
            email: values.email,
            phone: values.phone,
            message: values.installationProject,
          })
        }
      },
      ...(response.errors && {
        errors: errorsTranslated,
      }),
      ...(response.success && {
        success: t('register_success'),
      }),
    }),
    [
      t,
      matches,
      profiles,
      specialities,
      isShownPassword,
      response.pending,
      response.errors,
      response.success,
      register,
      handleSubmitForm,
      responseYoungDoctor?.pending,
      errorsTranslated,
    ]
  )

  return props
}

export default useRegisterFormProps
